import { Tab, Tabs } from '@mui/material';
import { InsightsDateRangePicker } from 'components/filter/modules/insights';
import { CountrySelect } from 'components/filter/input/CountrySelect';
import { BrandSelect } from 'components/filter/input/BrandSelect';
import { MarketplaceSelect } from 'components/filter/input/MarketplaceSelect';
import { FilterDialogButton } from 'components/filter/advancedFilter';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { PageContainer, PageHeader, Tile } from 'components/Layout/PageContainer';
import { FormattedMessage, useIntl } from 'react-intl';
import { TbIcon } from 'components/Figma';
import { Tooltip } from 'components/Tooltip';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Filters } from 'services/data.service';
import { PerformanceTab } from 'pages/entities/components/PerformanceTab';
import { Attribute } from 'services/model/model.service';
import { trackMatomoEvent } from 'services/matomo.service';
import { getTrackingPage, MATOMO_ACTIONS } from 'utils/matomoTracking';
import { ProfitabilityTab } from 'pages/entities/components/ProfitabilityTab';
import { InfoButton } from 'components/InfoButton';
import { PriceTab } from 'pages/entities/components/PriceTab';
import { TenantConfigContext } from 'context/TenantConfigContext';

const TAB_NAMES = {
  visibility: 'Visibility',
  performance: 'Performance',
  price: 'Price',
  profitability: 'Profitability',
  stock: 'Stock',
} as const;

export interface EntityPageProps {
  children: ReactNode;
  filterDecorator: (filter: Filters) => Filters;
  availableTables: Attribute[];
  showCostOfGoods?: boolean;
}

export function EntityPage({ children, filterDecorator, availableTables, showCostOfGoods = true }: EntityPageProps) {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { isSuperAdmin } = useContext(TenantConfigContext);

  const { tab } = useParams();

  const changeTab = (newTab: string) => {
    const untabbedPath = location.pathname.replace(tab ?? '', '');
    const newUrl = `${untabbedPath}${untabbedPath.endsWith('/') ? '' : '/'}${newTab}${location.search}`;
    navigate(newUrl);
  };

  const [tabComponent, setTabComponent] = useState<ReactNode>();

  useEffect(() => {
    switch (tab) {
      case 'performance':
        setTabComponent(<PerformanceTab filterDecorator={filterDecorator} availableTables={availableTables} />);
        break;
      case 'profitability':
        setTabComponent(
          <ProfitabilityTab
            filterDecorator={filterDecorator}
            availableTables={availableTables}
            showCostOfGoods={showCostOfGoods}
          />
        );
        break;
      case 'price':
        if (isSuperAdmin) {
          setTabComponent(<PriceTab filterDecorator={filterDecorator} />);
          break;
        }
        changeTab('performance');
        break;
      default:
        changeTab('performance');
    }
  }, [tab, isSuperAdmin]);

  const handleChange = (_event: React.SyntheticEvent, newValue: keyof typeof TAB_NAMES) => {
    trackEntityTabInteraction(newValue);
    changeTab(newValue);
  };

  const trackEntityTabInteraction = (tabKey: keyof typeof TAB_NAMES) => {
    trackMatomoEvent({
      category: getTrackingPage(location.pathname),
      action: MATOMO_ACTIONS.TAB_SWITCH,
      name: TAB_NAMES[tabKey],
    });
  };

  const lockedTab = (tabKey: keyof typeof TAB_NAMES) => (
    <Tooltip
      tooltipContent={intl.formatMessage({ id: 'entities.lens.coming.soon' })}
      onClick={() => trackEntityTabInteraction(tabKey)}
    >
      <div className="w-full cursor-pointer">
        <Tab
          disabled
          className="pointer-events-none"
          label={
            <div className="flex items-center w-full" title={'entities.lens.coming.soon'}>
              <div className={'w-full'}>
                <FormattedMessage id={`entities.lens.${tabKey}`} />
                <TbIcon name="LockClosed" className="inline-block ml-1" />
              </div>
            </div>
          }
          value={tab}
        />
      </div>
    </Tooltip>
  );

  return (
    <div className="insights-pro">
      <PageHeader topRightComponent={<InfoButton showText />}>
        <InsightsDateRangePicker.Default caption="filter.key.selectedDateRange" />
        <InsightsDateRangePicker.Comparison caption="filter.key.selectedDateRangeComparison" />
        <CountrySelect />
        <BrandSelect />
        <MarketplaceSelect />
        <FilterDialogButton module="insights" />
      </PageHeader>

      <div className="px-10">
        <PageContainer>
          {children}

          <Tile>
            <Tabs value={tab} onChange={handleChange} sx={{ paddingInlineStart: 0, paddingInlineEnd: 0 }}>
              {lockedTab('visibility')}
              <Tab
                label={intl.formatMessage({ id: 'entities.lens.performance' })}
                value="performance"
                className="hover:bg-denim-50 font-bold"
              />
              {isSuperAdmin ? (
                <Tab
                  label={intl.formatMessage({ id: 'entities.lens.price' })}
                  value="price"
                  className="hover:bg-denim-50 font-bold"
                />
              ) : (
                lockedTab('price')
              )}
              <Tab
                label={intl.formatMessage({ id: 'entities.lens.profitability' })}
                value="profitability"
                className="hover:bg-denim-50 font-bold"
              />
              ){lockedTab('stock')}
            </Tabs>
          </Tile>
          {tabComponent}
        </PageContainer>
      </div>
    </div>
  );
}
